/** @jsxImportSource @emotion/react */
import { useTranslation } from 'react-i18next';
import { TableContainer, Table, TableBody, TableRow, TableCell } from '@mui/material';
import { tb } from 'components/layouts/Table';
import { ContentFlex } from 'components/layouts/ContentFlex';
import { ContentSection } from 'components/layouts/ContentSection';
import { SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import * as wjGrid from '@grapecity/wijmo.grid';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import CustomGrid from 'components/grids/CustomGrid';
import { ExcpRole } from 'models/admin/ExcpRole';
import { PageMode } from 'models/common/Common';
import { useMessageBar } from 'hooks/useMessageBar';
import { getExcpPrjList, getExcpRequestList, getExcpRoleist } from 'apis/admin/ExcpRole';
import useSessionStore from 'stores/useSessionStore';
import ExcpUserModal from 'components/modals/common/PartnerUserModal';
import { validate } from 'uuid';
import { ApproveRequestPageType } from 'pages/approves/ApproveRequestDetailCase';
import { selectRequestedAuth } from 'apis/approves/approves';
import { isEmpty } from 'lodash';
import _ from 'lodash';

/**
 * @desc 협력사 권한 결재 요청 목록
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2026.01.14   jihwan89       협력사 권한요청 시 기 결재 내역 조회 및 밸리데이션 추가
 */

interface Props {
  aprReqId: string;
  isReadOnly?: boolean;
  mode: PageMode;
  onChangePrj?: (val: any[]) => void;
  onChangeRole?: (val: any[]) => void;
}

const ExcpAuthRequest = ({ aprReqId, mode, onChangePrj, onChangeRole, isReadOnly }: Props, ref) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { userId, empNm } = useSessionStore();
  const userSession = useSessionStore();
  const [authListData, setAuthListData] = useState<ExcpRole[]>([]);
  const [projectListData, setProjectListData] = useState<ExcpRole[]>([]);
  const [isAnyChecked, setIsAnyChecked] = useState(false);
  const [isOpenUserModal, setOpenUserModal] = useState<boolean>(false);
  const [requestData, setRequestData] = useState<ExcpRole>();
  const [requestedAuthData, setRequestedAuthData] = useState<ExcpRole>();
  const isAnyCheckedRef = useRef(isAnyChecked);

  const prjGridRef = useRef<wjGrid.FlexGrid | null>(null);
  const [canSelectPrj, setCanSelectPrj] = useState(false);
  const canSelectPrjRef = useRef(false);

  useImperativeHandle(ref, () => ({
    validate: () => {
      if (!_.isEmpty(requestedAuthData)) {
        return openMessageBar({
          type: 'error',
          content:
            `${t('com.label.진행중인 결재가 있습니다.', '진행중인 결재가 있습니다.')}` +
            ' ' +
            `${t('com.label.권한', '권한')}` +
            ': ' +
            requestedAuthData?.roleNm,
        });
      }

      return true;
    },
    getBizReqData: (aprReqId: string) => {
      // 저장
      const master = {
        castType: 'PartnerAuthRequest',
        aprReqId: aprReqId,
        excpId: requestData?.excpId,
        requestUserList: requestData?.requestUserList,
        requestRoleList: requestData?.requestRoleList,
        requestPrjList: requestData?.requestPrjList,
      } as ExcpRole;
      return master;
    },
  }));

  const authListDefinition = [
    {
      binding: 'roleNm',
      header: String(t('com.label.권한', '권한')),
      width: '*',
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'roleDesc',
      header: String(t('com.label.권한 설명', '권한 설명')),
      width: '*',
      isReadOnly: true,
      align: 'left',
    },
    {
      binding: 'prjUseYn',
      header: String(t('com.label.프로젝트 대상 여부', '프로젝트 대상 여부')),
      width: '*',
      isReadOnly: true,
      align: 'center',
    },
  ];

  const projectDefinition = [
    {
      binding: 'bildPlntCdNm',
      header: String(t('com.label.플랜트', '플랜트')),
      width: '*',
      isReadOnly: true,
      align: 'center',
    },
    {
      binding: 'elmPrjNm',
      header: String(t('com.label.프로젝트명', '프로젝트명')),
      width: '*',
      isReadOnly: true,
      align: 'left',
    },
  ];

  const getExcpRoleData = () => {
    getExcpRoleist().then((result: ExcpRole[]) => {
      if (result !== null) {
        setAuthListData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  const getExcpPrjData = () => {
    getExcpPrjList(String(requestData?.excpId)).then((result: ExcpRole[]) => {
      if (result) {
        setProjectListData(result);
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    });
  };

  useEffect(() => {
    if (!aprReqId || mode == 'WRITE') return;

    let alive = true;
    (async () => {
      const data = await getExcpRequestList(aprReqId);
      if (!alive) return;

      if (data) {
        setRequestData({ ...data });
      } else {
        openMessageBar({
          type: 'error',
          content: t(
            'com.msg.요청 정보 조회 중 오류가 발생했습니다.',
            '요청 정보 조회 중 오류가 발생했습니다.'
          ),
        });
      }
    })();

    return () => {
      alive = false;
    };
  }, [aprReqId, mode, isReadOnly]);

  const requestDataRef = useRef<ExcpRole | undefined>();
  useEffect(() => {
    requestDataRef.current = requestData; //prj 최신상태 업데이트
  }, [requestData, projectListData]);

  const prjInitialized = (grid: wjGrid.FlexGrid) => {
    prjGridRef.current = grid;
    grid.selectionChanging.addHandler((s, e) => {
      if (!canSelectPrjRef.current) {
        e.cancel = true;
      }
    });

    let firstRefreshed = true;
    grid.refreshed.addHandler((s, e) => {
      const current = requestDataRef.current;

      if (grid.rows.length === 0) return;
      // 최초 렌더링만 처리
      if (!firstRefreshed) return;
      firstRefreshed = false;
      if (!current) return;
      if (current.aprReqProgStatCd !== 'TMP') return;

      const prjList = current.requestPrjList ?? [];
      if (!prjList.length) return;

      const defaultSet = new Set(prjList.map((r) => r.elmPrjId));

      grid.rows.forEach((row, idx) => {
        const item = row.dataItem;
        row.isSelected = !!item && defaultSet.has(item.elmPrjId);
      });

      onChangePrj?.(prjList);

      grid.invalidate();
    });
  };

  const clearPrjSelection = () => {
    const grid = prjGridRef.current;
    if (!grid) return;

    grid.beginUpdate();
    grid.rows.forEach((row) => {
      row.isSelected = false;
    });
    grid.endUpdate();
    setRequestData((prev) => ({
      ...prev,
      requestPrjList: [],
      requestRoleList: [],
    }));
    onChangePrj?.([]);

    grid.invalidate();
  };

  const authInitialized = useCallback(
    (grid: wjGrid.FlexGrid) => {
      if (requestData?.aprReqProgStatCd !== 'TMP') return;

      const defaultSet = new Set((requestData?.requestRoleList ?? []).map((r) => r.roleCd));
      const hasPrjUseY =
        requestData?.requestRoleList?.some((item) => item.prjUseYn === 'Y') ?? false;

      if (hasPrjUseY) {
        setCanSelectPrj(true);
      } else {
        setCanSelectPrj(false);
      }

      grid.rows.forEach((row) => {
        const item = row.dataItem;
        row.isSelected = !!item && defaultSet.has(item.roleCd);
      });

      onChangeRole?.(requestData?.requestRoleList ?? []);

      grid.invalidate();
    },
    [requestData?.requestRoleList]
  );

  useEffect(() => {
    canSelectPrjRef.current = canSelectPrj;
  }, [canSelectPrj]);

  useEffect(() => {
    isAnyCheckedRef.current = isAnyChecked;
  }, [isAnyChecked]);

  useEffect(() => {
    getExcpRoleData();
    requestedAuth();

    const requestUsers = [
      {
        authReqUserId: userId,
        authReqUserNm: empNm,
      },
    ];

    if (mode == 'WRITE') {
      setRequestData((prev) => ({
        ...prev,
        excpId: userSession?.deptCd,
        excpNm: userSession?.deptNm,
        requestUserList: requestUsers,
      }));
    }
  }, []);

  const requestedAuth = async () => {
    // [25.01.14] jihwan89 - 기존 결재진행중인 권한요청이 있으면 결재요청 불가
    const condition = {
      optValCtn1: ApproveRequestPageType.EXCP_AUTH_REQ,
    };
    const requestedAuth = await selectRequestedAuth(condition);
    if (!_.isEmpty(requestedAuth)) {
      setRequestedAuthData({
        aprReqId: requestedAuth.aprReqId,
        roleNm: requestedAuth.optValCtn5,
      });
    }
  };

  useEffect(() => {
    if (requestData?.excpId) {
      getExcpPrjData();
    }
  }, [requestData?.excpId]);

  const handleOpenUserModal = () => {
    setOpenUserModal(true);
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('com.label.결재 정보', '결재 정보')}</h3>
        </SubTitleGroup>
      </SubTitleLayout>
      <TableContainer css={tb.table}>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell>{t('com.label.협력업체명', '협력업체명')}</TableCell>
              <TableCell>{requestData?.excpNm}</TableCell>
              <TableCell>{t('com.label.신청 인원', '신청 인원')}</TableCell>
              <TableCell width={650}>
                {requestData?.aprReqProgStatCd == 'TMP' || mode == 'WRITE' ? (
                  <CustomInputWithSearch
                    name="eqpNm"
                    className="find"
                    placeholder={String(
                      t('com.label.신청 인원를 입력해 주세요.', '신청 인원를 입력해 주세요.')
                    )}
                    value={
                      requestData?.requestUserList?.map((u) => u.authReqUserNm).join(', ') ?? ''
                    }
                    onSearchClick={handleOpenUserModal}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        handleOpenUserModal();
                      }
                    }}
                    readOnly={true}
                    width={300}
                  />
                ) : (
                  requestData?.requestUserList?.map((u) => u.authReqUserNm).join(', ') ?? ''
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <ContentFlex>
        <ContentSection className="section width50p marginT0">
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('com.label.권한 목록정보', '권한 목록정보')}</h3>
            </SubTitleGroup>
          </SubTitleLayout>
          <CustomGrid
            key={`auth-${requestData?.aprReqProgStatCd ?? 'none'}`}
            layoutDefinition={authListDefinition}
            rowData={
              requestData?.aprReqProgStatCd == 'TMP' || mode == 'WRITE'
                ? authListData
                : requestData?.requestRoleList ?? []
            }
            isSelector={requestData?.aprReqProgStatCd == 'TMP' || mode == 'WRITE' ? true : false}
            initialized={authInitialized}
            height={300}
            onChangeCheckedItem={(checkedItems) => {
              const hasChecked = Array.isArray(checkedItems)
                ? checkedItems.length > 0
                : !!checkedItems;
              setIsAnyChecked(hasChecked);

              const requestAuth = checkedItems.map(({ roleCd }) => ({ roleCd }));
              setRequestData((prev) => ({ ...prev, requestRoleList: requestAuth }));
              onChangeRole?.(checkedItems);

              const hasPrjUseY = checkedItems.some((item) => item.prjUseYn === 'Y');

              if (hasPrjUseY) {
                setCanSelectPrj(true);
              } else {
                clearPrjSelection();
                setCanSelectPrj(false);
              }
            }}
          />
        </ContentSection>
        <ContentSection className="section width50p marginT0">
          <SubTitleLayout>
            <SubTitleGroup>
              <h3>{t('com.label.프로젝트 정보', '프로젝트 정보')}</h3>
            </SubTitleGroup>
          </SubTitleLayout>
          <CustomGrid
            key={`prj-${requestData?.aprReqProgStatCd ?? 'none'}`}
            layoutDefinition={projectDefinition}
            rowData={
              requestData?.aprReqProgStatCd == 'TMP' || mode == 'WRITE'
                ? projectListData
                : requestData?.requestPrjList ?? []
            }
            isSelector={requestData?.aprReqProgStatCd == 'TMP' || mode == 'WRITE' ? true : false}
            height={300}
            initialized={prjInitialized}
            onChangeCheckedItem={(checkedItems) => {
              if (!canSelectPrjRef.current) return;
              const requestPrj = checkedItems.map(({ elmPrjId }) => ({ elmPrjId }));
              setRequestData((prev) => ({ ...prev, requestPrjList: requestPrj }));
              onChangePrj?.(requestPrj);
            }}
          />
        </ContentSection>
      </ContentFlex>
      {isOpenUserModal && (
        <ExcpUserModal
          open={isOpenUserModal}
          close={() => setOpenUserModal(false)}
          singleSelect={false}
          isFixExcp
          condition={{ excpId: requestData?.excpId }}
          onCallback={(result) => {
            const users = Array.isArray(result) ? result : result ? [result] : [];
            const requestUsers = users.map(({ userId, userNm }) => ({
              authReqUserId: userId,
              authReqUserNm: userNm,
            }));
            setRequestData((prev) => ({
              ...prev,
              requestUserList: requestUsers,
            }));
          }}
        />
      )}
    </>
  );
};

export default forwardRef(ExcpAuthRequest);
